import { useMemo } from "react";

import { useFileManagerStore } from "@/state/fileManagerStore";

/** Janela de páginas ao redor da atual, com elipses nas pontas. */
function pageWindow(current: number, pageCount: number): (number | "gap")[] {
  if (pageCount <= 7) return Array.from({ length: pageCount }, (_, index) => index);

  const pages = new Set<number>([0, pageCount - 1, current]);
  for (const offset of [-1, 1]) {
    const candidate = current + offset;
    if (candidate > 0 && candidate < pageCount - 1) pages.add(candidate);
  }
  const sorted = [...pages].sort((a, b) => a - b);

  const out: (number | "gap")[] = [];
  let previous: number | null = null;
  for (const page of sorted) {
    if (previous !== null && page - previous > 1) out.push("gap");
    out.push(page);
    previous = page;
  }
  return out;
}

const BUTTON =
  "rounded border border-ink-600 bg-ink-800 px-2.5 py-1 text-xs text-ink-100 " +
  "hover:border-emerald-600 hover:bg-ink-700 disabled:cursor-not-allowed disabled:opacity-40";

export function AssetPagination({ loadedCount }: { loadedCount: number }) {
  const page = useFileManagerStore((state) => state.page);
  const pageSize = useFileManagerStore((state) => state.pageSize);
  const totalAssets = useFileManagerStore((state) => state.totalAssets);
  const setPage = useFileManagerStore((state) => state.setPage);

  // Sem total exato (filtro de status ativo), navegamos só com anterior/próxima:
  // "próxima" existe enquanto a página veio cheia.
  const pageCount = totalAssets === null ? null : Math.max(1, Math.ceil(totalAssets / pageSize));
  const hasNext = pageCount === null ? loadedCount >= pageSize : page < pageCount - 1;
  const pages = useMemo(
    () => (pageCount === null ? [] : pageWindow(page, pageCount)),
    [page, pageCount],
  );

  if (pageCount !== null && pageCount <= 1) return null;
  if (pageCount === null && page === 0 && !hasNext) return null;

  const rangeStart = page * pageSize + 1;
  const rangeEnd = page * pageSize + loadedCount;

  return (
    <nav
      className="flex flex-wrap items-center justify-between gap-3 border-t border-ink-700 px-6 py-3"
      aria-label="Paginação de arquivos"
    >
      <span className="text-xs text-ink-400">
        {loadedCount === 0
          ? "Nenhum arquivo"
          : totalAssets === null
            ? `${rangeStart}–${rangeEnd}`
            : `${rangeStart}–${rangeEnd} de ${totalAssets}`}
      </span>

      <div className="flex flex-wrap items-center gap-1">
        <button
          type="button"
          className={BUTTON}
          onClick={() => setPage(page - 1)}
          disabled={page === 0}
        >
          Anterior
        </button>

        {pages.map((entry, index) =>
          entry === "gap" ? (
            <span key={`gap-${index}`} className="px-1 text-xs text-ink-500">
              …
            </span>
          ) : (
            <button
              key={entry}
              type="button"
              aria-current={entry === page ? "page" : undefined}
              className={
                entry === page
                  ? "rounded border border-emerald-600 bg-ink-700 px-2.5 py-1 text-xs font-medium text-ink-50"
                  : BUTTON
              }
              onClick={() => setPage(entry)}
            >
              {entry + 1}
            </button>
          ),
        )}

        <button type="button" className={BUTTON} onClick={() => setPage(page + 1)} disabled={!hasNext}>
          Próxima
        </button>
      </div>
    </nav>
  );
}
